import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp } from "lucide-react";

// Floating "back to top" button.
//
// Pass `scrollRef` when the screen scrolls inside its own container (any
// element with `overflow-y-auto`) — e.g. BuyerHome, BuyerCategories,
// CategoryScreen, ProductDetailsView. Omit it for screens that rely on the
// page/window scrolling instead (e.g. BuyerOrdersView, BuyerProfileView),
// and this falls back to listening on `window` / scrolling the window.
//
// Rendered with `position: fixed`, so it sits in the bottom-right corner of
// whichever screen it's mounted in, above the bottom nav bar (`bottom-24`
// matches the `pb-24` scroll padding already used throughout the app).
export default function ScrollToTopButton({
  scrollRef,
  threshold = 300,
  className = "",
}: {
  scrollRef?: React.RefObject<HTMLElement | null>;
  threshold?: number;
  className?: string;
}) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const el = scrollRef?.current;
    const target: HTMLElement | Window = el || window;

    const getScrollTop = () =>
      el ? el.scrollTop : window.scrollY || document.documentElement.scrollTop;

    const handleScroll = () => setVisible(getScrollTop() > threshold);
    handleScroll();

    target.addEventListener("scroll", handleScroll, { passive: true } as any);
    return () => target.removeEventListener("scroll", handleScroll as any);
    // scrollRef.current is intentionally read fresh above rather than
    // being a dependency itself — refs don't trigger re-renders when they
    // change, so we just re-check on mount / when the ref object changes.
  }, [scrollRef, threshold]);

  const handleClick = () => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={handleClick}
          aria-label="Scroll to top"
          initial={{ opacity: 0, scale: 0.7, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.7, y: 10 }}
          transition={{ type: "spring", stiffness: 400, damping: 28 }}
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.05 }}
          className={`fixed z-40 bottom-24 right-4 w-11 h-11 rounded-full bg-[#1006C5] hover:bg-[#0D0599] text-white shadow-lg shadow-black/40 flex items-center justify-center border border-white/10 ${className}`}
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
